import React, { useState } from "react";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";

/**
 * Parental authorisation config (multi-category).
 *
 * A ticket type can require parental authorisation for ONE OR MORE age
 * categories (e.g. a bilhete that goes from 8 to 80 years old can require
 * parental auth only for the "child_under_12" AND "teen_13_17" sub-ranges).
 *
 * Props:
 *   value:    full ticket type snapshot (uses `requires_parental_auth`,
 *             `parental_categories`, legacy `age_category`, `age_min`, `age_max`)
 *   onChange: receives a *partial* delta to merge into parent state
 *   defaultOpen: whether the section starts expanded
 */

const CATEGORY_DEFS = [
  { id: "child_under_12", label: "Crianças ≤ 12 anos (com Adulto Acompanhante)", min: 1, max: 12 },
  { id: "teen_13_17",     label: "Menores 13–17 anos (com Adulto Responsável)",   min: 13, max: 17 },
];

const recomputeRange = (cats) => {
  const sel = CATEGORY_DEFS.filter((c) => cats.includes(c.id));
  if (sel.length === 0) return { age_min: null, age_max: null };
  return {
    age_min: Math.min(...sel.map((c) => c.min)),
    age_max: Math.max(...sel.map((c) => c.max)),
  };
};

export const ParentalAuthConfigSection = ({ value, onChange, defaultOpen = false }) => {
  const v = value || {};
  // Back-compat: derive categories from legacy `age_category` if list is empty.
  const categories = (v.parental_categories && v.parental_categories.length)
    ? v.parental_categories
    : (v.age_category && v.age_category !== "adult" ? [v.age_category] : []);

  const [open, setOpen] = useState(defaultOpen || !!v.requires_parental_auth);

  const set = (delta) => onChange && onChange(delta);

  const toggleCategory = (catId, checked) => {
    const next = checked
      ? Array.from(new Set([...(categories || []), catId]))
      : (categories || []).filter((c) => c !== catId);
    const range = recomputeRange(next);
    set({
      parental_categories: next,
      // Keep legacy field aligned with the *first* selected category for
      // back-compat with code paths that still read `age_category`.
      age_category: next[0] || "adult",
      ...range,
    });
  };

  const onToggle = (enabled) => {
    if (enabled && categories.length === 0) {
      // Default: enable both categories so admins see a working configuration.
      const next = ["child_under_12", "teen_13_17"];
      const range = recomputeRange(next);
      set({
        requires_parental_auth: true,
        parental_categories: next,
        age_category: next[0],
        ...range,
      });
    } else {
      set({ requires_parental_auth: enabled });
    }
  };

  const badge = () => {
    if (!categories.length) return "ON";
    if (categories.length === 2) return "≤12 + 13–17";
    return categories[0] === "child_under_12" ? "≤ 12 anos" : "13–17";
  };

  return (
    <div className="border border-white/10 rounded-lg bg-white/[0.02]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 rounded-lg transition-colors"
        data-testid="parental-config-toggle"
      >
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-cyan-400" />
          <span className="text-zinc-200 text-sm font-medium">Autorização Parental (Menores)</span>
          {v.requires_parental_auth && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
              {badge()}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-white/5">
          <div className="flex items-center justify-between bg-white/[0.03] rounded-lg px-3 py-2.5">
            <div>
              <Label className="text-zinc-300">Requer autorização parental</Label>
              <p className="text-zinc-500 text-xs">Quando ativado, o formulário só será solicitado se a idade indicada pelo comprador corresponder a uma das categorias abaixo.</p>
            </div>
            <Switch
              checked={!!v.requires_parental_auth}
              onCheckedChange={onToggle}
              data-testid="parental-config-required"
            />
          </div>

          {v.requires_parental_auth && (
            <>
              <div>
                <Label className="text-zinc-300">Categorias etárias que requerem autorização</Label>
                <p className="text-zinc-500 text-xs mt-1 mb-2">
                  Pode selecionar uma ou ambas. Apenas compradores cuja idade caia nestas faixas serão obrigados a preencher o termo.
                </p>
                <div className="space-y-2">
                  {CATEGORY_DEFS.map((c) => {
                    const checked = categories.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className="flex items-start gap-3 bg-white/[0.03] rounded-lg px-3 py-2 cursor-pointer hover:bg-white/5 transition-colors"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(val) => toggleCategory(c.id, !!val)}
                          className="mt-0.5"
                          data-testid={`parental-config-cat-${c.id}`}
                        />
                        <div className="flex-1">
                          <span className="text-zinc-200 text-sm">{c.label}</span>
                          <span className="block text-zinc-500 text-xs">Idades {c.min}–{c.max}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300">Idade mínima (menor)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="17"
                    value={v.age_min ?? ""}
                    onChange={(e) => set({ age_min: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                    className="input-dark mt-2"
                    placeholder="—"
                    data-testid="parental-config-age-min"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Idade máxima (menor)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="17"
                    value={v.age_max ?? ""}
                    onChange={(e) => set({ age_max: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                    className="input-dark mt-2"
                    placeholder="—"
                    data-testid="parental-config-age-max"
                  />
                </div>
              </div>
              <p className="text-zinc-500 text-xs">
                ⚠️ Se a idade introduzida pelo comprador estiver fora destas categorias (ex: adulto a comprar para si próprio), o formulário de autorização parental não será exibido.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
